import type { TenantInstance } from '@omni/instance'

// Accent default from @omni/ui tokens.css. An instance whose accent matches
// it needs NO override — and skipping the injection matters: tokens.css also
// carries a prefers-color-scheme dark accent (#8a7dff), which a blanket
// :root override would clobber. So the default EDNA instance injects nothing
// and keeps the stock light/dark behaviour byte-identical.
const TOKEN_ACCENT = '#6654f5'

const STYLE_ID = 'omni-instance-theme'
const FONT_LINK_ID = 'omni-instance-font'

/** Stock --font-sans fallback stack from @omni/ui tokens.css. */
const TOKEN_FONT_STACK =
  'var(--font-geist-sans, "Poppins", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif)'

/**
 * Apply the instance's brand to the document via CSS custom properties —
 * the same mechanism the mail vendor's TenantThemeMount uses (a mounted
 * <style> tag), kept minimal: only diverging values are written, so every
 * token not overridden keeps its @omni/ui default.
 *
 * Also brands the document chrome itself (EDNA-3017): tab title from
 * copy.productName, theme-color from the accent, an optional per-instance
 * favicon, and an optional brand font prepended to --font-sans. The default
 * enterprise-dna instance sets none of these, so it stays byte-identical to
 * the stock shell.
 */
// The light-palette values for every token the dark @media block in
// @omni/ui tokens.css overrides (accent tokens excluded — those are the
// instance's own). Re-asserted on bare :root from the LAST stylesheet in the
// document, they win the cascade in both schemes, pinning the shell light.
const LIGHT_SCHEME_DECLS = [
  'color-scheme: light',
  '--background: #f8f9fb',
  '--foreground: #0b0c18',
  '--color-surface-0: #f8f9fb',
  '--color-surface-1: #ffffff',
  '--color-surface-2: #f1f5f9',
  '--color-surface-3: #e8ecf1',
  '--color-border: #e2e8f0',
  '--color-border-muted: #f1f5f9',
  '--color-text-primary: #0b0c18',
  '--color-text-secondary: #475569',
  '--color-text-muted: #5f6d80',
  '--color-danger: #ef4444',
  '--color-danger-muted: #fef2f2',
  '--color-success: #10b981',
  '--color-success-muted: #f0fdf4',
  '--color-warning: #f59e0b',
  '--color-warning-muted: #fffbeb',
  '--color-brand-amber-muted: rgba(var(--color-brand-amber-rgb), 0.12)',
  '--color-brand-pink: rgb(var(--color-brand-pink-rgb))',
  '--color-brand-pink-muted: rgba(var(--color-brand-pink-rgb), 0.12)',
  '--color-brand-purple-muted: rgba(var(--color-brand-purple-rgb), 0.12)',
  '--shadow-xs: 0 1px 2px 0 rgb(0 0 0 / 0.03)',
  '--shadow-sm: 0 1px 3px 0 rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)',
  '--shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.07), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
  '--shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.08), 0 4px 6px -4px rgb(0 0 0 / 0.04)',
  '--shadow-card: 0 0 60px rgba(35, 52, 115, 0.08), 0 1px 2px rgba(35, 52, 115, 0.04)',
  '--shadow-card-hover: 0 0 80px rgba(35, 52, 115, 0.14), 0 2px 4px rgba(35, 52, 115, 0.06)',
]

export function applyInstanceTheme(instance: TenantInstance): void {
  const { accent, accentMuted, gradient, faviconUrl, fontFamily, fontUrl } =
    instance.theme
  const decls: string[] = []

  // Light-pin FIRST so the instance's own accent/gradient/font overrides
  // below stay the last word within the injected block.
  if (instance.theme.forceLightScheme) decls.push(...LIGHT_SCHEME_DECLS)

  if (accent.toLowerCase() !== TOKEN_ACCENT) {
    decls.push(`--color-accent: ${accent}`)
    decls.push(`--color-accent-hover: ${accent}`)
    if (accentMuted) decls.push(`--color-accent-muted: ${accentMuted}`)
  }
  if (gradient) {
    decls.push(`--gradient-brand: ${gradient}`)
  }
  if (fontFamily) {
    decls.push(`--font-sans: "${fontFamily}", ${TOKEN_FONT_STACK}`)
  }

  // Tab title + theme-color: only for instances that name a product — the
  // index.html defaults ("Omni Command", #6654f5) stay for the stock shell.
  const productName = instance.copy?.productName
  if (productName) {
    document.title = productName
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', accent)
  }

  // Per-instance favicon (e.g. the TMM mascot on a red tile).
  if (faviconUrl) {
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (icon) icon.href = faviconUrl
  }

  // Brand font stylesheet (loaded once, before the override style lands).
  if (fontUrl && !document.getElementById(FONT_LINK_ID)) {
    const link = document.createElement('link')
    link.id = FONT_LINK_ID
    link.rel = 'stylesheet'
    link.href = fontUrl
    document.head.appendChild(link)
  }

  document.getElementById(STYLE_ID)?.remove()
  if (decls.length === 0) return

  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `:root {\n  ${decls.join(';\n  ')};\n}\n`
  document.head.appendChild(style)
}
